// src/components/Caixa/FecharCaixa.jsx
// ============================================================================
// COMPONENTE: Dialog para fechamento de caixa e registro de pagamentos
// ============================================================================
// Funcionalidades:
// - Registrar múltiplas formas de pagamento
// - Aplicar acréscimos e descontos
// - Usar crédito do paciente
// - Visualizar resumo do pagamento
// - Integração com API para fechar caixa
// - Design responsivo (Desktop 2 colunas, Mobile 1 coluna)
// ============================================================================

import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { useAuth } from "../../AuthContext";
import {
  FaCashRegister,
  FaShoppingCart,
  FaPlusCircle,
  FaMinusCircle,
  FaCreditCard,
  FaDollarSign,
} from "react-icons/fa";

const FecharCaixa = ({
  visible,
  onHide,
  pagamentoSelecionado,
  onSuccess,
  onError,
  onImprimirRecibo,
  onFechado,
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // ==========================================================================
  // ESTADOS DO FORMULÁRIO
  // ==========================================================================
  const [pagamentosAdicionados, setPagamentosAdicionados] = useState([]);
  const [formaPagamentoSelecionada, setFormaPagamentoSelecionada] =
    useState(null);
  const [valorPagamento, setValorPagamento] = useState("");
  const [saldoRestante, setSaldoRestante] = useState(0);

  // ==========================================================================
  // ESTADOS PARA AJUSTES
  // ==========================================================================
  const [acrescimo, setAcrescimo] = useState("");
  const [desconto, setDesconto] = useState("");
  const [mostrarAjustes, setMostrarAjustes] = useState(false);

  // ==========================================================================
  // DETECÇÃO DE DISPOSITIVO MÓVEL
  // ==========================================================================
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // ==========================================================================
  // CALCULAR SALDO RESTANTE
  // ==========================================================================
  useEffect(() => {
    if (pagamentoSelecionado) {
      const valorBase = parseFloat(pagamentoSelecionado.valor_total) || 0;
      const valorAcrescimo = parseFloat(acrescimo.replace(",", ".")) || 0;
      const valorDesconto = parseFloat(desconto.replace(",", ".")) || 0;

      const totalPagamentosAdicionados = pagamentosAdicionados.reduce(
        (acc, p) => acc + p.valor,
        0,
      );

      const valorTotalAjustado = valorBase + valorAcrescimo - valorDesconto;
      const novoSaldoRestante = valorTotalAjustado - totalPagamentosAdicionados;

      setSaldoRestante(novoSaldoRestante < 0 ? 0 : novoSaldoRestante);
    }
  }, [pagamentoSelecionado, pagamentosAdicionados, acrescimo, desconto]);

  // ==========================================================================
  // RESETAR CAMPOS AO FECHAR
  // ==========================================================================
  useEffect(() => {
    if (!visible) {
      resetarCampos();
    }
  }, [visible]);

  // ==========================================================================
  // FUNÇÃO: Resetar todos os campos do formulário
  // ==========================================================================
  const resetarCampos = () => {
    setPagamentosAdicionados([]);
    setFormaPagamentoSelecionada(null);
    setValorPagamento("");
    setAcrescimo("");
    setDesconto("");
    setMostrarAjustes(false);
  };

  // ==========================================================================
  // FUNÇÃO: Formatar valor para envio à API
  // ==========================================================================
  const formatarValorParaEnvio = (valor) => {
    if (!valor) return "0.00";
    return parseFloat(valor.replace(",", ".")).toFixed(2);
  };

  // ==========================================================================
  // HANDLER: Adicionar pagamento
  // ==========================================================================
  const handleAdicionarPagamento = () => {
    if (!formaPagamentoSelecionada) {
      if (onError) onError("Selecione uma forma de pagamento");
      return;
    }

    const valor = parseFloat(valorPagamento.replace(",", "."));
    if (!valor || valor <= 0) {
      if (onError) onError("Informe um valor válido");
      return;
    }

    const isCreditoPaciente =
      formaPagamentoSelecionada.descricao?.toLowerCase() === "crédito paciente";

    if (isCreditoPaciente) {
      const saldoDisponivel =
        parseFloat(pagamentoSelecionado.paciente_saldo) || 0;
      const totalCreditoJaUsado = pagamentosAdicionados
        .filter((p) => p.isCreditoPaciente)
        .reduce((acc, p) => acc + p.valor, 0);
      const saldoAtualDisponivel = saldoDisponivel - totalCreditoJaUsado;

      if (valor > saldoAtualDisponivel) {
        if (onError) {
          onError(
            `Saldo insuficiente! Crédito disponível: R$ ${saldoAtualDisponivel.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`,
          );
        }
        return;
      }
    }

    if (valor > saldoRestante + 0.01) {
      if (onError) {
        onError(
          `Valor maior que o saldo restante da conta (R$ ${saldoRestante.toLocaleString("pt-BR", { minimumFractionDigits: 2 })})`,
        );
      }
      return;
    }

    const novoPagamento = {
      id: Date.now(),
      forma_pagamento_id: formaPagamentoSelecionada.id,
      forma_pagamento_descricao: formaPagamentoSelecionada.descricao,
      valor: valor,
      isCreditoPaciente: isCreditoPaciente,
    };

    setPagamentosAdicionados([...pagamentosAdicionados, novoPagamento]);
    setFormaPagamentoSelecionada(null);
    setValorPagamento("");
  };

  // ==========================================================================
  // HANDLER: Remover pagamento
  // ==========================================================================
  const handleRemoverPagamento = (id) => {
    setPagamentosAdicionados(pagamentosAdicionados.filter((p) => p.id !== id));
  };

  // ==========================================================================
  // HANDLER: Confirmar e finalizar pagamentos
  // ==========================================================================
  const handleConfirmarPagamentos = async () => {
    setLoading(true);

    try {
      const valorAcrescimoNum = parseFloat(acrescimo.replace(",", ".")) || 0;
      const valorDescontoNum = parseFloat(desconto.replace(",", ".")) || 0;
      const valorTotalOriginal =
        parseFloat(pagamentoSelecionado.valor_total) || 0;

      const novoValorTotal =
        valorTotalOriginal + valorAcrescimoNum - valorDescontoNum;

      if (saldoRestante > 0.01) {
        if (onError) {
          onError(
            `Não é possível fechar o caixa! Saldo restante: R$ ${saldoRestante.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}.\nO valor total deve ser totalmente pago.`,
          );
        }
        setLoading(false);
        return;
      }

      if (novoValorTotal > 0.01 && pagamentosAdicionados.length === 0) {
        if (onError) {
          onError(
            `Adicione pelo menos uma forma de pagamento para finalizar.\n\nValor total: R$ ${novoValorTotal.toFixed(2)}`,
          );
        }
        setLoading(false);
        return;
      }

      // Sem pagamentos (valor zero)
      if (pagamentosAdicionados.length === 0) {
        const dadosEnvio = {
          caixa_id: Number(pagamentoSelecionado.id),
          agendamento_id: Number(pagamentoSelecionado.agendamento_id),
          valor_acrescimo: formatarValorParaEnvio(acrescimo),
          valor_desconto: formatarValorParaEnvio(desconto),
          valor_total: novoValorTotal.toFixed(2),
          valor_pago: "0.00",
        };

        const response = await fetch(
          "https://helder.cantarellisolucoes.com.br/wp-json/custom/v1/caixas/fechar",
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${user.token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(dadosEnvio),
          },
        );

        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(data.message || "Erro ao processar pagamento");
        }

        resetarCampos();
        onHide();
        if (onFechado) onFechado();
        if (onSuccess) {
          onSuccess("Caixa fechado com sucesso!");
        }
        setLoading(false);
        return;
      }

      // Múltiplos pagamentos - UMA REQUISIÇÃO POR PAGAMENTO (igual ao original)
      let pagamentosProcessados = 0;
      let totalCreditoUsado = 0;
      const saldoPacienteOriginal =
        parseFloat(pagamentoSelecionado.paciente_saldo) || 0;

      for (const pagamento of pagamentosAdicionados) {
        // Verificar crédito paciente antes de cada pagamento
        const isCreditoPaciente = pagamento.isCreditoPaciente;
        if (isCreditoPaciente) {
          const saldoDisponivel = saldoPacienteOriginal - totalCreditoUsado;
          if (pagamento.valor > saldoDisponivel) {
            throw new Error(
              `Saldo insuficiente para pagamento em crédito! Disponível: R$ ${saldoDisponivel.toFixed(2)}`,
            );
          }
          totalCreditoUsado += pagamento.valor;
        }

        const dadosEnvio = {
          caixa_id: Number(pagamentoSelecionado.id),
          agendamento_id: Number(pagamentoSelecionado.agendamento_id),
          forma_pagamento_id: Number(pagamento.forma_pagamento_id),
          valor_acrescimo: formatarValorParaEnvio(acrescimo),
          valor_desconto: formatarValorParaEnvio(desconto),
          valor_total: novoValorTotal.toFixed(2),
          valor_pago: pagamento.valor.toFixed(2), // 🔥 Cada pagamento individual
        };

        const response = await fetch(
          "https://helder.cantarellisolucoes.com.br/wp-json/custom/v1/caixas/fechar",
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${user.token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(dadosEnvio),
          },
        );

        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(
            data.message ||
              `Erro ao processar pagamento ${pagamento.forma_pagamento_descricao}`,
          );
        }

        pagamentosProcessados++;
      }

      const totalPago = pagamentosAdicionados.reduce(
        (acc, p) => acc + p.valor,
        0,
      );

      const reciboData = {
        ...pagamentoSelecionado,
        pagamentos: pagamentosAdicionados.map((p) => ({
          forma_pagamento_descricao: p.forma_pagamento_descricao,
          valor: p.valor,
          isCreditoPaciente: p.isCreditoPaciente,
        })),
        valor_pago: totalPago,
        valor_acrescimo: valorAcrescimoNum,
        valor_desconto: valorDescontoNum,
        valor_total_final: novoValorTotal,
      };

      resetarCampos();
      onHide();
      if (onFechado) onFechado();
      if (onSuccess) {
        onSuccess("Pagamento(s) realizado(s) com sucesso!", reciboData);
      }
    } catch (err) {
      console.error("❌ Erro ao processar pagamento:", err);
      if (onError) {
        onError(err.message || "Erro inesperado ao processar pagamento");
      }
    } finally {
      setLoading(false);
    }
  };

  // ==========================================================================
  // VALORES PARA EXIBIÇÃO
  // ==========================================================================
  if (!pagamentoSelecionado) return null;

  const valorBase = parseFloat(pagamentoSelecionado.valor_total) || 0;
  const valorAcrescimo = parseFloat(acrescimo.replace(",", ".")) || 0;
  const valorDesconto = parseFloat(desconto.replace(",", ".")) || 0;
  const valorTotalAjustado = valorBase + valorAcrescimo - valorDesconto;
  const totalPago = pagamentosAdicionados.reduce((acc, p) => acc + p.valor, 0);
  const totalCreditoPacienteUsado = pagamentosAdicionados
    .filter((p) => p.isCreditoPaciente)
    .reduce((acc, p) => acc + p.valor, 0);
  const saldoPacienteAtual =
    (parseFloat(pagamentoSelecionado.paciente_saldo) || 0) -
    totalCreditoPacienteUsado;

  // ==========================================================================
  // ESTILOS COMPARTILHADOS
  // ==========================================================================
  const inputStyle = {
    width: "100%",
    height: "40px",
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: "0.9rem",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    padding: "0 12px",
    transition: "all 0.2s",
    outline: "none",
  };

  const iconStyle = {
    color: "#B83732",
    fontSize: "1rem",
    minWidth: "20px",
  };

  const labelStyle = {
    fontWeight: "600",
    color: "#4a5568",
    fontSize: "0.85rem",
    letterSpacing: "0.3px",
    marginBottom: "6px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  };

  // ==========================================================================
  // TEMPLATES PARA DROPDOWNS
  // ==========================================================================
  const dropdownItemTemplate = (option) => (
    <span
      style={{
        fontFamily: "'Rajdhani', sans-serif",
        fontSize: "0.9rem",
        padding: "8px 12px",
      }}
    >
      {option.descricao}
    </span>
  );

  const dropdownValueTemplate = (option, props) => {
    if (!option)
      return (
        <span
          style={{
            fontFamily: "'Rajdhani', sans-serif",
            fontSize: "0.9rem",
            color: "#7f8c8d",
          }}
        >
          {props.placeholder}
        </span>
      );
    return (
      <span
        style={{
          fontFamily: "'Rajdhani', sans-serif",
          fontSize: "0.9rem",
        }}
      >
        {option.descricao}
      </span>
    );
  };

  // ==========================================================================
  // RENDER PRINCIPAL
  // ==========================================================================
  return (
    <Dialog
      visible={visible}
      style={{
        width: "90vw",
        maxWidth: "1020px",
        fontFamily: "'Rajdhani', sans-serif",
      }}
      onHide={() => {
        if (!loading) {
          resetarCampos();
          onHide();
        }
      }}
      draggable={false}
      header={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "-25px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              width: "100%",
            }}
          >
            <div style={{ marginRight: "12px", paddingLeft: "8px" }}>
              <FaCashRegister
                style={{ fontSize: "2.4rem", color: "#B83732" }}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
              <div
                style={{
                  fontSize: "0.85rem",
                  width: "100%",
                  letterSpacing: "1px",
                  color: "#718096",
                }}
              >
                FECHAR
              </div>
              <div
                style={{
                  fontSize: "1.8rem",
                  fontWeight: "bold",
                  marginTop: "-8px",
                  color: "#2d3748",
                }}
              >
                PAGAMENTO
              </div>
            </div>
          </div>
        </div>
      }
    >
      <div style={{ padding: "10px 0" }}>
        {/* 🔥 MOBILE: Layout em coluna única */}
        {isMobile ? (
          <>
            {/* CARD ÚNICO PARA MOBILE */}
            <div
              style={{
                backgroundColor: "#FAFAFA",
                border: "1px solid #e2e8f0",
                borderRadius: "12px",
                padding: "20px",
                marginBottom: "20px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              }}
            >
              {/* Valor Total */}
              <div style={{ marginBottom: "20px" }}>
                <div style={labelStyle}>
                  <FaShoppingCart style={iconStyle} />
                  <span>VALOR TOTAL</span>
                  <Button
                    label="+ -"
                    icon={
                      mostrarAjustes ? "pi pi-chevron-up" : "pi pi-chevron-down"
                    }
                    onClick={() => setMostrarAjustes(!mostrarAjustes)}
                    className="p-button-text p-button-sm"
                    style={{
                      marginLeft: "auto",
                      padding: "2px 10px",
                      fontSize: "0.7rem",
                      color: "#B83732",
                    }}
                  />
                </div>
                <div
                  style={{
                    fontSize: "1.8rem",
                    fontWeight: "bold",
                    color: "#27ae60",
                    textAlign: "center",
                    padding: "10px",
                    borderRadius: "8px",
                    backgroundColor: "white",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  R${" "}
                  {valorTotalAjustado.toLocaleString("pt-BR", {
                    minimumFractionDigits: 2,
                  })}
                </div>

                {mostrarAjustes && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                      marginTop: "10px",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          fontSize: "0.75rem",
                          color: "#4a5568",
                          marginBottom: "4px",
                          display: "block",
                        }}
                      >
                        <FaPlusCircle
                          style={{
                            color: "#28a745",
                            fontSize: "0.7rem",
                            marginRight: "4px",
                          }}
                        />
                        Acréscimo (R$)
                      </label>
                      <InputText
                        value={acrescimo}
                        onChange={(e) => setAcrescimo(e.target.value)}
                        placeholder="0,00"
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          fontSize: "0.75rem",
                          color: "#4a5568",
                          marginBottom: "4px",
                          display: "block",
                        }}
                      >
                        <FaMinusCircle
                          style={{
                            color: "#dc3545",
                            fontSize: "0.7rem",
                            marginRight: "4px",
                          }}
                        />
                        Desconto (R$)
                      </label>
                      <InputText
                        value={desconto}
                        onChange={(e) => setDesconto(e.target.value)}
                        placeholder="0,00"
                        style={inputStyle}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Saldo Restante */}
              <div style={{ marginBottom: "20px" }}>
                <div style={labelStyle}>
                  <FaCashRegister style={iconStyle} />
                  <span>SALDO RESTANTE</span>
                </div>
                <div
                  style={{
                    fontSize: "1.8rem",
                    fontWeight: "bold",
                    color: saldoRestante <= 0 ? "#27ae60" : "#B83732",
                    textAlign: "center",
                    padding: "10px",
                    borderRadius: "8px",
                    backgroundColor: "white",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  R${" "}
                  {saldoRestante.toLocaleString("pt-BR", {
                    minimumFractionDigits: 2,
                  })}
                </div>
              </div>

              {/* Crédito Paciente */}
              {pagamentoSelecionado.paciente_saldo > 0 && (
                <div style={{ marginBottom: "20px" }}>
                  <div style={labelStyle}>
                    <FaDollarSign style={iconStyle} />
                    <span>CRÉDITO DO PACIENTE</span>
                  </div>
                  <div
                    style={{
                      fontSize: "1.4rem",
                      fontWeight: "bold",
                      color: saldoPacienteAtual < 0 ? "#e74c3c" : "#3498DB",
                      textAlign: "center",
                      backgroundColor: "white",
                      padding: "10px",
                      borderRadius: "8px",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    R${" "}
                    {saldoPacienteAtual.toLocaleString("pt-BR", {
                      minimumFractionDigits: 2,
                    })}
                  </div>
                </div>
              )}

              {/* Formas de Pagamento */}
              <div>
                <div style={labelStyle}>
                  <FaCreditCard style={iconStyle} />
                  <span>FORMAS DE PAGAMENTO</span>
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                    marginBottom: "15px",
                  }}
                >
                  <Dropdown
                    value={formaPagamentoSelecionada}
                    onChange={(e) => setFormaPagamentoSelecionada(e.value)}
                    options={pagamentoSelecionado.formas_pagamento_disponiveis}
                    optionLabel="descricao"
                    placeholder="Selecione a forma"
                    style={{
                      width: "100%",
                      height: "40px",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                    }}
                    itemTemplate={dropdownItemTemplate}
                    valueTemplate={dropdownValueTemplate}
                    appendTo={document.body}
                  />
                  <div style={{ display: "flex", gap: "8px" }}>
                    <InputText
                      value={valorPagamento}
                      onChange={(e) => setValorPagamento(e.target.value)}
                      placeholder="Valor"
                      style={{
                        flex: 1,
                        height: "40px",
                        textAlign: "center",
                        ...inputStyle,
                      }}
                    />
                    <Button
                      icon="pi pi-plus"
                      onClick={handleAdicionarPagamento}
                      style={{
                        height: "40px",
                        width: "40px",
                        minWidth: "40px",
                        backgroundColor: "#28a745",
                        borderColor: "#28a745",
                        color: "white",
                        borderRadius: "8px",
                      }}
                      disabled={
                        loading || !formaPagamentoSelecionada || !valorPagamento
                      }
                      loading={loading}
                    />
                  </div>
                </div>

                {/* Lista de Pagamentos Adicionados */}
                {pagamentosAdicionados.length > 0 ? (
                  pagamentosAdicionados.map((pagamento, index) => (
                    <div
                      key={pagamento.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "10px",
                        backgroundColor: "white",
                        borderRadius: "8px",
                        border: "1px solid #e2e8f0",
                        marginBottom: "8px",
                      }}
                    >
                      <div
                        style={{
                          width: "24px",
                          height: "24px",
                          backgroundColor: "#B83732",
                          color: "white",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.8rem",
                        }}
                      >
                        {index + 1}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: "600" }}>
                          {pagamento.forma_pagamento_descricao}
                          {pagamento.isCreditoPaciente && (
                            <span
                              style={{
                                fontSize: "0.7rem",
                                backgroundColor: "#3498DB",
                                color: "white",
                                padding: "2px 6px",
                                borderRadius: "3px",
                                marginLeft: "8px",
                              }}
                            >
                              Saldo
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: "0.8rem", color: "#7f8c8d" }}>
                          R$ {pagamento.valor.toFixed(2)}
                        </div>
                      </div>
                      <Button
                        icon="pi pi-trash"
                        onClick={() => handleRemoverPagamento(pagamento.id)}
                        className="p-button-danger p-button-sm"
                        style={{ width: "32px", height: "32px" }}
                      />
                    </div>
                  ))
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "30px",
                      backgroundColor: "white",
                      border: "2px dashed #e2e8f0",
                      borderRadius: "8px",
                    }}
                  >
                    <FaCreditCard
                      style={{
                        fontSize: "2rem",
                        color: "#cbd5e1",
                        marginBottom: "8px",
                      }}
                    />
                    <p
                      style={{
                        fontSize: "0.85rem",
                        color: "#6c757d",
                        margin: 0,
                      }}
                    >
                      Nenhum pagamento adicionado
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* RESUMO DO PAGAMENTO PARA MOBILE */}
            <div
              style={{
                padding: "20px",
                borderRadius: "12px",
                border: "1px solid #ffeaa7",
                backgroundColor: "#fff8e1",
                display: "flex",
                flexDirection: "column",
                gap: "16px",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <div
                  style={{
                    fontSize: "1.3rem",
                    fontWeight: "700",
                    color: "#2d3748",
                  }}
                >
                  RESUMO DO PAGAMENTO
                </div>
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: "#718096",
                    marginTop: "4px",
                  }}
                >
                  {new Date().toLocaleDateString("pt-BR", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </div>
              </div>

              <div>
                <div
                  style={{
                    fontWeight: "600",
                    color: "#B83732",
                    borderBottom: "1px solid #B83732",
                    paddingBottom: "4px",
                    marginBottom: "8px",
                  }}
                >
                  PACIENTE
                </div>
                <div style={{ fontSize: "0.85rem", color: "#4a5568" }}>
                  <div>
                    <strong>Nome:</strong> {pagamentoSelecionado.paciente_nome}
                  </div>
                  <div>
                    <strong>CPF:</strong> {pagamentoSelecionado.paciente_cpf}
                  </div>
                  <div>
                    <strong>Telefone:</strong>{" "}
                    {pagamentoSelecionado.paciente_telefone}
                  </div>
                </div>
              </div>

              <div>
                <div
                  style={{
                    fontWeight: "600",
                    color: "#B83732",
                    borderBottom: "1px solid #B83732",
                    paddingBottom: "4px",
                    marginBottom: "8px",
                  }}
                >
                  SERVIÇOS
                </div>
                {pagamentoSelecionado.servico_principal?.map((servico, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "0.8rem",
                      marginBottom: "4px",
                    }}
                  >
                    <span>{servico.descricao}</span>
                    <span>R$ {parseFloat(servico.valor).toFixed(2)}</span>
                  </div>
                ))}
                {pagamentoSelecionado.servicos_adicionais?.map(
                  (servico, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: "0.8rem",
                        marginBottom: "4px",
                      }}
                    >
                      <span>{servico.descricao}</span>
                      <span>R$ {parseFloat(servico.valor).toFixed(2)}</span>
                    </div>
                  ),
                )}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    borderTop: "1px dashed #718096",
                    marginTop: "6px",
                    paddingTop: "6px",
                    fontWeight: "600",
                  }}
                >
                  <span>Subtotal</span>
                  <span>
                    R${" "}
                    {valorBase.toLocaleString("pt-BR", {
                      minimumFractionDigits: 2,
                    })}
                  </span>
                </div>
              </div>

              <div>
                <div
                  style={{
                    fontWeight: "600",
                    color: "#B83732",
                    borderBottom: "1px solid #B83732",
                    paddingBottom: "4px",
                    marginBottom: "8px",
                  }}
                >
                  PAGAMENTOS
                </div>

                <div style={{ marginBottom: "10px" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "0.75rem",
                      marginBottom: "4px",
                    }}
                  >
                    <span>Progresso:</span>
                    <span>
                      R$ {totalPago.toFixed(2)} de R${" "}
                      {valorTotalAjustado.toFixed(2)}
                    </span>
                  </div>
                  <div
                    style={{
                      width: "100%",
                      height: "6px",
                      backgroundColor: "#e2e8f0",
                      borderRadius: "3px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: `${Math.min(100, (totalPago / valorTotalAjustado) * 100)}%`,
                        height: "100%",
                        backgroundColor:
                          saldoRestante <= 0 ? "#27ae60" : "#B83732",
                      }}
                    />
                  </div>
                </div>

                {pagamentosAdicionados.map((p) => (
                  <div
                    key={p.id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "0.8rem",
                      marginBottom: "4px",
                    }}
                  >
                    <span>
                      {p.forma_pagamento_descricao}
                      {p.isCreditoPaciente && " (Crédito)"}
                    </span>
                    <span>R$ {p.valor.toFixed(2)}</span>
                  </div>
                ))}

                {(valorAcrescimo > 0 || valorDesconto > 0) && (
                  <div
                    style={{
                      marginTop: "6px",
                      paddingTop: "6px",
                      borderTop: "1px dashed #e2e8f0",
                    }}
                  >
                    {valorAcrescimo > 0 && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: "0.8rem",
                        }}
                      >
                        <span style={{ color: "#28a745" }}>Acréscimo</span>
                        <span style={{ color: "#28a745" }}>
                          + R$ {valorAcrescimo.toFixed(2)}
                        </span>
                      </div>
                    )}
                    {valorDesconto > 0 && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: "0.8rem",
                        }}
                      >
                        <span style={{ color: "#dc3545" }}>Desconto</span>
                        <span style={{ color: "#dc3545" }}>
                          - R$ {valorDesconto.toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    borderTop: "1px dashed #718096",
                    marginTop: "8px",
                    paddingTop: "8px",
                    fontWeight: "600",
                  }}
                >
                  <span>Saldo Final</span>
                  <span>R$ {saldoRestante.toFixed(2)}</span>
                </div>
              </div>

              <div
                style={{
                  paddingTop: "12px",
                  borderTop: "1px solid #ffeaa7",
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "0.7rem",
                  color: "#718096",
                }}
              >
                <div>
                  <div style={{ fontWeight: "500" }}>
                    {pagamentoSelecionado.empresa_nome}
                  </div>
                  <div style={{ fontSize: "0.65rem" }}>
                    {pagamentoSelecionado.empresa_cidade} -{" "}
                    {pagamentoSelecionado.empresa_estado}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontWeight: "500" }}>
                    {pagamentoSelecionado.profissional_nome}
                  </div>
                  <div style={{ fontSize: "0.65rem" }}>
                    {pagamentoSelecionado.profissional_especialidade}
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          /* DESKTOP: Layout em 2 colunas */
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "20px",
            }}
          >
            {/* LADO ESQUERDO - CARD ÚNICO */}
            <div
              style={{
                backgroundColor: "#FAFAFA",
                border: "1px solid #e2e8f0",
                borderRadius: "12px",
                padding: "20px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              }}
            >
              {/* Valor Total */}
              <div style={{ marginBottom: "20px" }}>
                <div style={labelStyle}>
                  <FaShoppingCart style={iconStyle} />
                  <span>VALOR TOTAL</span>
                  <Button
                    label="+ -"
                    icon={
                      mostrarAjustes ? "pi pi-chevron-up" : "pi pi-chevron-down"
                    }
                    onClick={() => setMostrarAjustes(!mostrarAjustes)}
                    className="p-button-text p-button-sm"
                    style={{
                      marginLeft: "auto",
                      padding: "2px 10px",
                      fontSize: "0.7rem",
                      color: "#B83732",
                    }}
                  />
                </div>
                <div
                  style={{
                    fontSize: "1.8rem",
                    fontWeight: "bold",
                    color: "#27ae60",
                    textAlign: "center",
                    padding: "10px",
                    borderRadius: "8px",
                    backgroundColor: "white",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  R${" "}
                  {valorTotalAjustado.toLocaleString("pt-BR", {
                    minimumFractionDigits: 2,
                  })}
                </div>

                {mostrarAjustes && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "10px",
                      marginTop: "10px",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          fontSize: "0.75rem",
                          color: "#4a5568",
                          marginBottom: "4px",
                          display: "block",
                        }}
                      >
                        <FaPlusCircle
                          style={{
                            color: "#28a745",
                            fontSize: "0.7rem",
                            marginRight: "4px",
                          }}
                        />
                        Acréscimo (R$)
                      </label>
                      <InputText
                        value={acrescimo}
                        onChange={(e) => setAcrescimo(e.target.value)}
                        placeholder="0,00"
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          fontSize: "0.75rem",
                          color: "#4a5568",
                          marginBottom: "4px",
                          display: "block",
                        }}
                      >
                        <FaMinusCircle
                          style={{
                            color: "#dc3545",
                            fontSize: "0.7rem",
                            marginRight: "4px",
                          }}
                        />
                        Desconto (R$)
                      </label>
                      <InputText
                        value={desconto}
                        onChange={(e) => setDesconto(e.target.value)}
                        placeholder="0,00"
                        style={inputStyle}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Saldo Restante */}
              <div style={{ marginBottom: "20px" }}>
                <div style={labelStyle}>
                  <FaCashRegister style={iconStyle} />
                  <span>SALDO RESTANTE</span>
                </div>
                <div
                  style={{
                    fontSize: "1.8rem",
                    fontWeight: "bold",
                    color: saldoRestante <= 0 ? "#27ae60" : "#B83732",
                    textAlign: "center",
                    padding: "10px",
                    borderRadius: "8px",
                    backgroundColor: "white",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  R${" "}
                  {saldoRestante.toLocaleString("pt-BR", {
                    minimumFractionDigits: 2,
                  })}
                </div>
              </div>

              {/* Crédito Paciente */}
              {pagamentoSelecionado.paciente_saldo > 0 && (
                <div style={{ marginBottom: "20px" }}>
                  <div style={labelStyle}>
                    <FaDollarSign style={iconStyle} />
                    <span>CRÉDITO DO PACIENTE</span>
                  </div>
                  <div
                    style={{
                      fontSize: "1.4rem",
                      fontWeight: "bold",
                      color: saldoPacienteAtual < 0 ? "#e74c3c" : "#3498DB",
                      textAlign: "center",
                      backgroundColor: "white",
                      padding: "10px",
                      borderRadius: "8px",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    R${" "}
                    {saldoPacienteAtual.toLocaleString("pt-BR", {
                      minimumFractionDigits: 2,
                    })}
                  </div>
                </div>
              )}

              {/* Formas de Pagamento */}
              <div>
                <div style={labelStyle}>
                  <FaCreditCard style={iconStyle} />
                  <span>FORMAS DE PAGAMENTO</span>
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    marginBottom: "15px",
                    alignItems: "center",
                  }}
                >
                  <Dropdown
                    value={formaPagamentoSelecionada}
                    onChange={(e) => setFormaPagamentoSelecionada(e.value)}
                    options={pagamentoSelecionado.formas_pagamento_disponiveis}
                    optionLabel="descricao"
                    placeholder="Selecione a forma"
                    style={{
                      flex: 1,
                      height: "40px",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                    }}
                    itemTemplate={dropdownItemTemplate}
                    valueTemplate={dropdownValueTemplate}
                    appendTo={document.body}
                  />
                  <InputText
                    value={valorPagamento}
                    onChange={(e) => setValorPagamento(e.target.value)}
                    placeholder="Valor"
                    style={{
                      width: "120px",
                      height: "40px",
                      textAlign: "start",
                      fontFamily: "'Rajdhani', sans-serif",
                      fontSize: "0.9rem",
                      borderRadius: "8px",
                      border: "1px solid #e2e8f0",
                    }}
                  />
                  <Button
                    icon="pi pi-plus"
                    onClick={handleAdicionarPagamento}
                    className="p-button-success p-button-sm"
                    style={{
                      height: "40px",
                      width: "40px",
                      minWidth: "40px",
                      color: "white",
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    disabled={
                      loading || !formaPagamentoSelecionada || !valorPagamento
                    }
                    loading={loading}
                  />
                </div>

                {/* Lista de Pagamentos Adicionados */}
                {pagamentosAdicionados.length > 0 ? (
                  pagamentosAdicionados.map((pagamento, index) => (
                    <div
                      key={pagamento.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "10px",
                        backgroundColor: "white",
                        borderRadius: "8px",
                        border: "1px solid #e2e8f0",
                        marginBottom: "8px",
                      }}
                    >
                      <div
                        style={{
                          width: "24px",
                          height: "24px",
                          backgroundColor: "#B83732",
                          color: "white",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.8rem",
                        }}
                      >
                        {index + 1}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: "600" }}>
                          {pagamento.forma_pagamento_descricao}
                          {pagamento.isCreditoPaciente && (
                            <span
                              style={{
                                fontSize: "0.7rem",
                                backgroundColor: "#3498DB",
                                color: "white",
                                padding: "2px 6px",
                                borderRadius: "3px",
                                marginLeft: "8px",
                              }}
                            >
                              Saldo
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: "0.8rem", color: "#7f8c8d" }}>
                          R$ {pagamento.valor.toFixed(2)}
                        </div>
                      </div>
                      <Button
                        icon="pi pi-trash"
                        onClick={() => handleRemoverPagamento(pagamento.id)}
                        className="p-button-danger p-button-sm"
                        style={{ width: "32px", height: "32px" }}
                      />
                    </div>
                  ))
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "30px",
                      backgroundColor: "white",
                      border: "2px dashed #e2e8f0",
                      borderRadius: "8px",
                    }}
                  >
                    <FaCreditCard
                      style={{
                        fontSize: "2rem",
                        color: "#cbd5e1",
                        marginBottom: "8px",
                      }}
                    />
                    <p
                      style={{
                        fontSize: "0.85rem",
                        color: "#6c757d",
                        margin: 0,
                      }}
                    >
                      Nenhum pagamento adicionado
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* LADO DIREITO - RESUMO DO PAGAMENTO */}
            <div
              style={{
                padding: "20px",
                borderRadius: "12px",
                border: "1px solid #ffeaa7",
                backgroundColor: "#fff8e1",
                display: "flex",
                flexDirection: "column",
                gap: "16px",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <div
                  style={{
                    fontSize: "1.3rem",
                    fontWeight: "700",
                    color: "#2d3748",
                  }}
                >
                  RESUMO DO PAGAMENTO
                </div>
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: "#718096",
                    marginTop: "4px",
                  }}
                >
                  {new Date().toLocaleDateString("pt-BR", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </div>
              </div>

              <div>
                <div
                  style={{
                    fontWeight: "600",
                    color: "#B83732",
                    borderBottom: "1px solid #B83732",
                    paddingBottom: "4px",
                    marginBottom: "8px",
                  }}
                >
                  PACIENTE
                </div>
                <div style={{ fontSize: "0.85rem", color: "#4a5568" }}>
                  <div>
                    <strong>Nome:</strong> {pagamentoSelecionado.paciente_nome}
                  </div>
                  <div>
                    <strong>CPF:</strong> {pagamentoSelecionado.paciente_cpf}
                  </div>
                  <div>
                    <strong>Telefone:</strong>{" "}
                    {pagamentoSelecionado.paciente_telefone}
                  </div>
                </div>
              </div>

              {/* SERVIÇOS */}
              <div>
                <div
                  style={{
                    fontWeight: "600",
                    color: "#B83732",
                    borderBottom: "1px solid #B83732",
                    paddingBottom: "4px",
                    marginBottom: "8px",
                  }}
                >
                  SERVIÇOS
                </div>

                {pagamentoSelecionado.servico_principal?.length > 0 ||
                pagamentoSelecionado.servicos_adicionais?.length > 0 ? (
                  <>
                    {pagamentoSelecionado.servico_principal?.map(
                      (servico, idx) => (
                        <div
                          key={idx}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: "0.8rem",
                            marginBottom: "4px",
                          }}
                        >
                          <span>{servico.descricao}</span>
                          <span>R$ {parseFloat(servico.valor).toFixed(2)}</span>
                        </div>
                      ),
                    )}
                    {pagamentoSelecionado.servicos_adicionais?.map(
                      (servico, idx) => (
                        <div
                          key={idx}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: "0.8rem",
                            marginBottom: "4px",
                          }}
                        >
                          <span>{servico.descricao}</span>
                          <span>R$ {parseFloat(servico.valor).toFixed(2)}</span>
                        </div>
                      ),
                    )}
                  </>
                ) : (
                  pagamentoSelecionado.itens?.map((item, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: "0.8rem",
                        marginBottom: "4px",
                      }}
                    >
                      <span>
                        {item.descricao || item.servico_nome}
                        {item.tipo === "original" && " (Principal)"}
                      </span>
                      <span>R$ {parseFloat(item.valor).toFixed(2)}</span>
                    </div>
                  ))
                )}

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    borderTop: "1px dashed #718096",
                    marginTop: "6px",
                    paddingTop: "6px",
                    fontWeight: "600",
                  }}
                >
                  <span>Subtotal</span>
                  <span>
                    R${" "}
                    {valorBase.toLocaleString("pt-BR", {
                      minimumFractionDigits: 2,
                    })}
                  </span>
                </div>
              </div>

              <div>
                <div
                  style={{
                    fontWeight: "600",
                    color: "#B83732",
                    borderBottom: "1px solid #B83732",
                    paddingBottom: "4px",
                    marginBottom: "8px",
                  }}
                >
                  PAGAMENTOS
                </div>

                <div style={{ marginBottom: "10px" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "0.75rem",
                      marginBottom: "4px",
                    }}
                  >
                    <span>Progresso:</span>
                    <span>
                      R$ {totalPago.toFixed(2)} de R${" "}
                      {valorTotalAjustado.toFixed(2)}
                    </span>
                  </div>
                  <div
                    style={{
                      width: "100%",
                      height: "6px",
                      backgroundColor: "#e2e8f0",
                      borderRadius: "3px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: `${Math.min(100, (totalPago / valorTotalAjustado) * 100)}%`,
                        height: "100%",
                        backgroundColor:
                          saldoRestante <= 0 ? "#27ae60" : "#B83732",
                      }}
                    />
                  </div>
                </div>

                {pagamentosAdicionados.map((p) => (
                  <div
                    key={p.id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "0.8rem",
                      marginBottom: "4px",
                    }}
                  >
                    <span>
                      {p.forma_pagamento_descricao}
                      {p.isCreditoPaciente && " (Crédito)"}
                    </span>
                    <span>R$ {p.valor.toFixed(2)}</span>
                  </div>
                ))}

                {(valorAcrescimo > 0 || valorDesconto > 0) && (
                  <div
                    style={{
                      marginTop: "6px",
                      paddingTop: "6px",
                      borderTop: "1px dashed #e2e8f0",
                    }}
                  >
                    {valorAcrescimo > 0 && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: "0.8rem",
                        }}
                      >
                        <span style={{ color: "#28a745" }}>Acréscimo</span>
                        <span style={{ color: "#28a745" }}>
                          + R$ {valorAcrescimo.toFixed(2)}
                        </span>
                      </div>
                    )}
                    {valorDesconto > 0 && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: "0.8rem",
                        }}
                      >
                        <span style={{ color: "#dc3545" }}>Desconto</span>
                        <span style={{ color: "#dc3545" }}>
                          - R$ {valorDesconto.toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    borderTop: "1px dashed #718096",
                    marginTop: "8px",
                    paddingTop: "8px",
                    fontWeight: "600",
                  }}
                >
                  <span>Saldo Final</span>
                  <span>R$ {saldoRestante.toFixed(2)}</span>
                </div>
              </div>

              <div
                style={{
                  paddingTop: "12px",
                  borderTop: "1px solid #ffeaa7",
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "0.7rem",
                  color: "#718096",
                }}
              >
                <div>
                  <div style={{ fontWeight: "500" }}>
                    {pagamentoSelecionado.empresa_nome}
                  </div>
                  <div style={{ fontSize: "0.65rem" }}>
                    {pagamentoSelecionado.empresa_cidade} -{" "}
                    {pagamentoSelecionado.empresa_estado}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontWeight: "500" }}>
                    {pagamentoSelecionado.profissional_nome}
                  </div>
                  <div style={{ fontSize: "0.65rem" }}>
                    {pagamentoSelecionado.profissional_especialidade}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ========== BOTÕES DE AÇÃO ========== */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "16px",
          flexWrap: "wrap",
          marginTop: "24px",
        }}
      >
        {/* Botão Cancelar */}
        <Button
          label="CANCELAR"
          icon="pi pi-times"
          onClick={() => {
            resetarCampos();
            onHide();
          }}
          className="p-button-outlined"
          style={{
            padding: "10px 28px",
            minWidth: "120px",
            borderColor: "#e2e8f0",
            color: "#718096",
            fontWeight: "600",
            borderRadius: "8px",
          }}
          disabled={loading}
        />

        {/* Botão Criar Agendamento */}
        <Button
          label={loading ? "PROCESSANDO..." : "FINALIZAR PAGAMENTOS"}
          icon="pi pi-check"
          onClick={handleConfirmarPagamentos}
          className="p-button-success p-button-sm"
          style={{
            padding: "10px 28px",
            minWidth: "180px",
            fontWeight: "600",
            borderRadius: "8px",
            color: "white",
          }}
          disabled={loading}
          loading={loading}
        />
      </div>
    </Dialog>
  );
};

export default FecharCaixa;
